import { useCallback, useEffect, useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import isEqual from 'lodash/isEqual';

import { actions as virtualCardActions } from '@ott/shared-virtual-card-logic';
import KM from '@ott/utility-km';

import { unwrapResult } from '@reduxjs/toolkit';

import { FORM_FIELDS, FORM_FIELDS_LIST } from '../helpers/constants';
import { FormData as FormDataType, PassportRecognitionResult, UpgradeError } from '../types';

type Props = {
  setValue: UseFormSetValue<FormDataType>;
};

let controller: AbortController;

export const usePasswordRecognitionForm = ({ setValue }: Props) => {
  const [isPassportRecognitionLoading, setIsPassportRecognitionLoading] = useState(false);
  const [isPassportRecognitionPartial, setIsPassportRecognitionPartial] = useState(false);
  const [passportRecognitionError, setPassportRecognitionError] = useState('');
  const [isPassportRecognitionSuccess, setIsPassportRecognitionSuccess] = useState(false);
  const [loadingTime, setLoadingTime] = useState(0);

  const dispatch = useDispatch<any>();

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isPassportRecognitionLoading) {
      timer = setInterval(() => {
        setLoadingTime((prevTime) => prevTime + 1);
      }, 1000);
    } else {
      setLoadingTime(0);
    }

    return () => {
      clearInterval(timer);
    };
  }, [isPassportRecognitionLoading]);

  const handleUploadPassportPhoto = useCallback(
    (file: File) => {
      setIsPassportRecognitionLoading(true);
      setIsPassportRecognitionPartial(false);
      setIsPassportRecognitionSuccess(false);
      setPassportRecognitionError('');

      controller = new AbortController();
      const signal = controller.signal;

      const formData = new FormData();
      formData.append('file', file);

      dispatch(virtualCardActions.passportRecognition({ formData, signal }))
        .then(unwrapResult)
        .then((result: PassportRecognitionResult) => {
          const fieldsArr = Object.keys(result.data);

          if (fieldsArr.length === 0) {
            KM('Virtual_Card_Identification_Photo_Recognition', {
              obj: { status: 'error', error: 'No fields recognized' },
            });

            return setPassportRecognitionError('error');
          }

          if (result.meta.error) {
            setPassportRecognitionError(result.meta.error);
            KM('Virtual_Card_Identification_Photo_Recognition', {
              obj: { status: 'error', error: result.meta.error },
            });
          } else if (!isEqual(FORM_FIELDS_LIST.sort(), fieldsArr.sort())) {
            setIsPassportRecognitionPartial(true);
            KM('Virtual_Card_Identification_Photo_Recognition', {
              obj: { status: 'partial', recognizedFields: fieldsArr },
            });
          } else if (isEqual(FORM_FIELDS_LIST.sort(), fieldsArr.sort())) {
            setIsPassportRecognitionSuccess(true);
            KM('Virtual_Card_Identification_Photo_Recognition', { obj: { status: 'full' } });
          }

          if (result.data.lastName) setValue(FORM_FIELDS.LAST_NAME, result.data.lastName);
          if (result.data.firstName) setValue(FORM_FIELDS.FIRST_NAME, result.data.firstName);
          if (result.data.middleName) setValue(FORM_FIELDS.MIDDLE_NAME, result.data.middleName);
          if (result.data.birthDate) setValue(FORM_FIELDS.BIRTH_DATE, result.data.birthDate);
          if (result.data.documentSeries && result.data.documentNumber) {
            setValue(
              FORM_FIELDS.DOCUMENT_NUMBER,
              `${result.data.documentSeries}${result.data.documentNumber}`
            );
          } else if (result.data.documentNumber) {
            setValue(FORM_FIELDS.DOCUMENT_NUMBER, result.data.documentNumber);
          } else if (result.data.documentSeries) {
            setValue(FORM_FIELDS.DOCUMENT_NUMBER, result.data.documentSeries);
          }
          if (result.data.documentIssueDate)
            setValue(FORM_FIELDS.DOCUMENT_ISSUE_DATE, result.data.documentIssueDate);
          if (result.data.documentUnitCode)
            setValue(FORM_FIELDS.DOCUMENT_UNIT_CODE, result.data.documentUnitCode);
        })
        .catch((err: UpgradeError) => {
          KM('Virtual_Card_Identification_Photo_Recognition', {
            obj: { status: 'error', error: err.message },
          });
          setPassportRecognitionError(err.message);
        })
        .finally(() => {
          setIsPassportRecognitionLoading(false);
        });
    },
    [dispatch, setValue]
  );

  const abortUploadPassportPhoto = () => {
    if (controller) {
      controller.abort();
      KM('Virtual_Card_Identification_Photo_Cancel', { obj: { waiting: loadingTime } });
      setIsPassportRecognitionLoading(false);
    }
  };

  return {
    passportRecognitionError,
    isPassportRecognitionPartial,
    isPassportRecognitionLoading,
    isPassportRecognitionSuccess,
    handleUploadPassportPhoto,
    abortUploadPassportPhoto,
  };
};
